import * as React from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import SettingsCard from '../../components/SettingsCard';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';

/*
 * System health — the thin port (per the resolved ADMIN-MERGE
 * decision): API hostname + disk usage on a 10s poll, and the
 * temp-images janitor done right — confirm dialog + a proper POST
 * (classic fired a bare GET with no confirm). A real multi-host ops
 * dashboard stays deferred as new product.
 */

export default function AdminSystemHealthV2() {
    const authenticatedApi = useAuthenticatedApi();

    const [usage, setUsage] = useState<{
        hostname?: string;
        diskUsage?: string;
    } | null>(null);
    const [failed, setFailed] = useState(false);
    const [clearing, setClearing] = useState(false);

    useEffect(() => {
        let mounted = true;
        const poll = async () => {
            const data = await authenticatedApi
                .get('/telemetry/server-resource-usage')
                .then((r) => r.data)
                .catch(() => null);
            if (!mounted) return;
            if (data) {
                setUsage(data);
                setFailed(false);
            } else if (!usage) {
                setFailed(true);
            }
        };
        poll();
        const interval = setInterval(poll, 10_000);
        return () => {
            mounted = false;
            clearInterval(interval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearTempImages = async () => {
        if (
            !window.confirm(
                'Delete all temp images on the API server? Files still being processed could be lost.',
            )
        )
            return;
        setClearing(true);
        const ok = await authenticatedApi
            .post('/telemetry/clear-temp-directory-images')
            .then(() => true)
            .catch(() => false);
        setClearing(false);
        if (ok) toast.success('Temp images deleted');
        else toast.error("Couldn't delete temp images.");
    };

    return (
        <SettingsCard
            title="API server"
            desc="Live resource usage, refreshed every 10 seconds."
            foot={
                <>
                    <span className="flex-1 text-[11.5px] text-faint">
                        temp images are working files from processing
                    </span>
                    <button
                        type="button"
                        disabled={clearing}
                        onClick={clearTempImages}
                        className="cursor-pointer rounded-full border border-border bg-transparent px-[18px] py-2 font-[inherit] text-[12.5px] font-semibold text-attention disabled:opacity-40"
                    >
                        {clearing ? 'Deleting…' : 'Delete temp images'}
                    </button>
                </>
            }
        >
            {failed ? (
                <p className="text-[13px] text-muted-foreground">
                    Couldn't reach the telemetry endpoint.
                </p>
            ) : (
                <div className="flex flex-wrap gap-9">
                    <div>
                        <div
                            className={
                                'text-[21px] font-bold leading-none tracking-[-0.01em] ' +
                                (usage ? '' : 'text-faint')
                            }
                        >
                            {usage?.hostname ?? '…'}
                        </div>
                        <div className="mt-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                            Hostname
                        </div>
                    </div>
                    <div>
                        <div
                            className={
                                'text-[21px] font-bold leading-none tracking-[-0.01em] tabular-nums ' +
                                (usage ? '' : 'text-faint')
                            }
                        >
                            {usage?.diskUsage ?? '…'}
                        </div>
                        <div className="mt-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-faint">
                            Disk usage
                        </div>
                    </div>
                </div>
            )}
        </SettingsCard>
    );
}
